export const SUPPORTED_PROTOCOLS = [
  { value: 'sftp', label: 'SFTP (SSH)', defaultPort: 22 },
  { value: 'ftps', label: 'FTPS (explicit TLS)', defaultPort: 21 },
  { value: 'ftp', label: 'FTP (plain)', defaultPort: 21 },
];

export const DATA_SOURCE_STEPS = new Set(['product_data_sync', 'inventory_sync']);

export function defaultDataSourceForm() {
  return {
    protocol: 'sftp',
    host: '',
    port: 22,
    username: '',
    password: '',
    remote_path: '/',
    file_pattern: '*',
    use_passive: true,
    use_tls: false,
  };
}

const SAVED_PASSWORD_MASK = '••••••••';

export { SAVED_PASSWORD_MASK };

/** Parse host field when user pastes a full URL (sftp://user@host/path). */
export function parseHostInput(host, { username = '', remotePath = '/', protocol = '' } = {}) {
  let h = String(host || '').trim();
  let user = String(username || '').trim();
  let path = String(remotePath || '/').trim() || '/';
  let proto = String(protocol || '').trim().toLowerCase();

  const stripSchemes = (value) => {
    let out = value;
    let changed = true;
    while (changed) {
      changed = false;
      for (const scheme of ['sftp', 'ftps', 'ftp']) {
        const prefix = `${scheme}://`;
        if (out.toLowerCase().startsWith(prefix)) {
          if (!proto) proto = scheme;
          out = out.slice(prefix.length).trimStart();
          changed = true;
          break;
        }
      }
    }
    return out;
  };

  h = stripSchemes(h);

  if (h.includes('@')) {
    const at = h.indexOf('@');
    const userPart = h.slice(0, at).trim();
    h = h.slice(at + 1).trim();
    if (userPart && !user) user = userPart;
    h = stripSchemes(h);
  }

  const slash = h.indexOf('/');
  if (slash >= 0) {
    const pathPart = h.slice(slash);
    h = h.slice(0, slash).trim();
    if (pathPart && (path === '/' || path === '')) {
      path = pathPart.startsWith('/') ? pathPart : `/${pathPart}`;
    }
  }

  if (h.includes(':') && !h.startsWith('[')) {
    const colon = h.lastIndexOf(':');
    const maybePort = h.slice(colon + 1);
    if (/^\d+$/.test(maybePort)) {
      h = h.slice(0, colon);
    }
  }

  return {
    host: h.replace(/\/+$/, ''),
    username: user,
    remote_path: path,
    protocol: proto,
  };
}

export function dataSourceToForm(source) {
  const base = defaultDataSourceForm();
  if (!source) return base;
  const parsed = parseHostInput(source.host, {
    username: source.username,
    remotePath: source.remote_path,
    protocol: source.protocol,
  });
  return {
    ...base,
    protocol: parsed.protocol || source.protocol || base.protocol,
    host: parsed.host || '',
    port: source.port ?? base.port,
    username: parsed.username || '',
    password: '',
    remote_path: parsed.remote_path || '/',
    file_pattern: source.file_pattern || '*',
    use_passive: source.use_passive !== false,
    use_tls: !!source.use_tls,
  };
}

/** Trim connection fields; password may come from form state or a DOM ref (autofill). */
export function connectionFieldsFromForm(form, passwordOverride) {
  const password = passwordOverride !== undefined
    ? String(passwordOverride)
    : String(form?.password ?? '');
  return {
    host: String(form?.host ?? '').trim(),
    username: String(form?.username ?? '').trim(),
    password,
    hasPassword: password.length > 0,
  };
}

export function canConnectDataSource(form, configuredSource, passwordOverride) {
  const { host, username, password, hasPassword } = connectionFieldsFromForm(
    form,
    passwordOverride,
  );
  const hasStoredPassword = Boolean(configuredSource?.has_password);
  return Boolean(host && username && (hasPassword || hasStoredPassword));
}

export function formToPayload(form, { includePassword = true, passwordOverride } = {}) {
  const parsed = parseHostInput(form.host, {
    username: form.username,
    remotePath: form.remote_path,
    protocol: form.protocol,
  });
  const out = {
    protocol: parsed.protocol || form.protocol,
    host: parsed.host,
    port: Number(form.port) || protocolDefaultPort(parsed.protocol || form.protocol),
    username: parsed.username,
    remote_path: parsed.remote_path,
    file_pattern: (form.file_pattern || '*').trim() || '*',
    use_passive: !!form.use_passive,
    use_tls: !!form.use_tls,
  };
  if (includePassword) {
    const pwd = passwordOverride !== undefined
      ? String(passwordOverride)
      : String(form.password ?? '');
    if (pwd.length > 0) {
      out.password = pwd;
    }
  }
  return out;
}

export function protocolDefaultPort(protocol) {
  const found = SUPPORTED_PROTOCOLS.find((p) => p.value === protocol);
  return found ? found.defaultPort : 22;
}

export function applyProtocolDefaults(form, protocol) {
  const port = protocolDefaultPort(protocol);
  return {
    ...form,
    protocol,
    port,
    use_tls: protocol === 'ftps',
  };
}
